import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { getActiveBanners } from '../services/bannersService';
import { buildAssetUrl } from '../utils/urlHelpers';

const BannerCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [banners, setBanners] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch banners from API
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        setIsLoading(true);
        const data = await getActiveBanners();
        // Transform image URLs to full URLs
        const bannersWithUrls = data.map(banner => ({
          ...banner,
          imageUrl: buildAssetUrl(banner.image_url)
        }));
        setBanners(bannersWithUrls);
      } catch (error) {
        console.error('Error fetching banners:', error);
        setBanners([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBanners();
  }, []);

  const goToNext = () => {
    if (banners.length === 0) return;
    setCurrentIndex((prevIndex) =>
      prevIndex === banners.length - 1 ? 0 : prevIndex + 1
    );
  };

  const goToPrev = () => {
    if (banners.length === 0) return;
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? banners.length - 1 : prevIndex - 1
    );
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const handleBannerClick = (banner) => {
    if (banner.link_url) {
      window.open(banner.link_url, '_blank');
    }
  };

  // Auto-rotate every 5 seconds
  useEffect(() => {
    if (banners.length <= 1) return;

    const interval = setInterval(() => {
      goToNext();
    }, 5000);

    return () => clearInterval(interval);
  }, [banners.length]);

  // Don't render if loading or no banners (need at least 2 for carousel to make sense)
  if (isLoading) {
    return null; // Don't show loading state, just hide
  }

  if (banners.length < 2) {
    return null; // Hide carousel if less than 2 banners
  }

  return (
    <div className="relative w-full h-40 overflow-hidden rounded-xl mb-6">
      {/* Banner Images */}
      <div
        className="flex transition-transform duration-500 ease-in-out h-full"
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
      >
        {banners.map((banner) => (
          <div
            key={banner.id}
            className={`w-full flex-shrink-0 ${banner.link_url ? 'cursor-pointer' : ''}`}
            onClick={() => handleBannerClick(banner)}
          >
            <img
              src={banner.imageUrl}
              alt={banner.title || 'Banner'}
              className="w-full h-full object-cover"
              onError={(e) => {
                // Hide the image on error
                e.target.style.display = 'none';
              }}
            />
            {/* Title overlay if present */}
            {banner.title && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                <p className="text-white font-medium text-sm">{banner.title}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={(e) => { e.stopPropagation(); goToPrev(); }}
        className="absolute left-3 top-1/2 transform -translate-y-1/2 bg-black/20 hover:bg-black/30 text-white p-2 rounded-full transition-all duration-200"
        aria-label="Previous banner"
      >
        <ChevronLeft size={20} />
      </button>

      <button
        onClick={(e) => { e.stopPropagation(); goToNext(); }}
        className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-black/20 hover:bg-black/30 text-white p-2 rounded-full transition-all duration-200"
        aria-label="Next banner"
      >
        <ChevronRight size={20} />
      </button>

      {/* Dots Indicator */}
      <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-2">
        {banners.map((_, index) => (
          <button
            key={index}
            onClick={(e) => { e.stopPropagation(); goToSlide(index); }}
            className={`w-2 h-2 rounded-full transition-all duration-200 ${
              index === currentIndex ? 'bg-white w-4' : 'bg-white/50'
            }`}
            aria-label={`Go to banner ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default BannerCarousel;
